import { createApp, nextTick } from 'vue';
import axios from 'axios';
import vueSlider from 'vue-slider-component';
import 'vue-slider-component/theme/default.css'
import Datepicker from 'vue3-datepicker';
import Snotify from 'vue3-snotify';
import Cookies from 'js-cookie';
import PriceFormatter from '../PriceFormatter';
import DateFormatter from '../DateFormatter';
import ScrollPage from '../ScrollPage';
import AddressList from '../components/address-list/address-list';

const priceCalculator = async () => {
  const el = document.querySelector('.bf-price-calculator');

  if (el) {
    const app = createApp({
      delimiters: ['${', '}'],
      components: {
        vueSlider,
        Datepicker,
        AddressList
      },
      data() {
        return {
          form: {
            enquiryId: null,
            selectedLocation: null,
            selectedSpace: null,
            firstName: null,
            lastName: null,
            email: null,
            phone: null,
            postcode: null,
            selectedTitle: 'Mr',
            agreement: false,
            duration: 1,
            sizeSlider: 0,
            moveInDate: DateFormatter(new Date(Date.now() + (24 * 60 * 60 * 1000))),
            fullPrice: 0,
            discountedPrice: 0
          },
          sharedComputer: false,
          formErrors: {},
          locationList: [],
          titleOptions: [
            { value: 'Mr' },
            { value: 'Mrs' },
            { value: 'Miss' },
            { value: 'Ms' }
          ],
          offices: [],
          sizeOptions: {
            dotSize: 24,
            height: 4,
            min: 0,
            max: 1,
            interval: 1,
            speed: 0.1,
            marks: true,
            tooltip: 'always',
            stepStyle: {
              width: '2px',
              height: '8px',
              visibility: 'visible'
            },
            process: true,
            formatter: '{value}',
          },
          durationOptions: [
            {
              id: 1,
              value: 'Up to <br/> 3 months'
            },
            {
              id: 2,
              value: '3-6 <br/>  months'
            },
            {
              id: 3,
              value: 'Over <br/> 6 months'
            },
          ],
          moveIn: {
            date: new Date(Date.now() + (24 * 60 * 60 * 1000)),
            lowerLimit: new Date()
          },
          discount: {
            percentage: 50,
            duration: 3
          },
          complete: false,
          steps: {
            1: {
              label: 'Register',
              state: 'loading',
              lock_on_comp: true,
            },
            2: {
              label: 'Calculate',
              state: 'loading',
              lock_on_comp: false,
            },
            3: {
              label: 'Confirm',
              state: 'init',
              lock_on_comp: false,
            }
          },
          currentStep: 1,
          addressList: [],
          showAddressFields: false,
          skippingStep1: false
        };
      },
      mounted() {
        this.fetchSites();
      },
      methods: {
        onSubmit() {
          if (this.steps[3].state === 'loading') {
            return;
          }

          this.formErrors = {};
          this.updateStepState(['3'], 'loading');
          this.reserve();
        },
        completeStep1() {
          const endpoint = '/actions/businessfirst/calculator/handle-registration';
          const params = this.getCsrfAwareParams(this.form);

          this.updateStepState(['1'], 'loading');

          return axios.post(endpoint, params).then((response) => {
            this.form.enquiryId = response.data.enquiryId;
            this.updateStepState(['1'], 'complete');
            this.gotoStep(2);
            this.trackConversion();

            if (response.data.crypt && !this.sharedComputer) {
              Cookies.set('previous_quote', response.data.crypt, { expires: 5 });
            }

            nextTick(() => {
              ScrollPage('step2');
            });
          }).catch((error) => {
            this.updateStepState(['1'], 'complete');
            this.handleValidationErrors(error);
          });
        },
        completeStep2() {
          this.updateStepState(['2'], 'complete');
          this.gotoStep(3);

          nextTick(() => {
            ScrollPage('step3');
          });
        },
        reserve() {
          const endpoint = '/actions/businessfirst/calculator/handle-submission';

          return axios.post(endpoint, this.getCsrfAwareParams(this.form))
              .then(response => {
                this.complete = true;
                this.updateStepState(['3'], 'complete');
                nextTick(() => {
                  ScrollPage('lastStep');
                });
              })
              .catch((error) => {
                this.showAddressFields = true;
                this.handleValidationErrors(error);
              });
        },
        getAddressList() {
          const { postcode } = this.form;

          if (postcode == null || postcode.length < 3) {
            this.formErrors.postcode = ['Please enter a valid postcode'];
            return;
          }
          delete this.formErrors.postcode;

          this.$refs.addressListComponent.reload(postcode);
        },
        toggleAddressFields() {
          this.showAddressFields = !this.showAddressFields;
        },
        updateAddress(address) {
          this.form.addressLine1 = address.address_line_1;
          this.form.addressLine2 = address.address_line_2;
          this.form.cityTown = address.city;
          this.form.county = address.county;
          this.form.postcode = address.postcode;
        },
        handleValidationErrors(error) {
          this.updateStepState(['3'], 'ready');

          this.$snotify.error('Invalid details provided. Please browse up and fix the fields marked with an error');

          this.formErrors = error.response.data.error;
        },
        gotoStep(theStep) {
          const prev = theStep - 1;
          const stepID = parseInt(theStep);
          const target = this.steps[stepID];

          if ((target.state == 'complete' && target.lock_on_comp == true) ||
              (typeof this.steps[prev] !== 'undefined' && this.steps[prev].state !== 'complete')) {
            return;
          }

          this.complete = false;
          this.currentStep = stepID;

          document.body.scrollTop = 0; // For Safari
          document.documentElement.scrollTop = 0;
        },
        backToCalculator() {
          this.gotoStep(2);
          ScrollPage('step2');
        },
        fetchSites() {
          const urlParams = new URLSearchParams(window.location.search);
          const params = this.getCsrfAwareParams();
          const promise = axios.post('/actions/businessfirst/calculator/office-list', params);

          promise.then(response => {
            this.handleSiteListResponse(response, urlParams);
          }).catch((error) => {
            this.$snotify.error(
                'Please refresh the page to try again. If the problem persists, please contact support',
                'Error'
            );
          });
        },
        handleSiteListResponse(response, urlParams) {
          const locationParam = urlParams.get('location');

          this.locationList = response.data;
          const defaultSelection = this.locationList[0];
          let location;

          if (!locationParam) {
            this.form.selectedLocation = defaultSelection;
            location = defaultSelection;
          } else {
            location = this.findLocationByName(locationParam);
          }

          this.form.selectedLocation = location;
          this.moveInDate = DateFormatter(this.moveIn.date);
        },
        handleUnitSizesResponse(response) {
          const sizes = response.data;
          const applicableSizes = this.form.selectedLocation.sizes;
          const priceOverrides = this.form.selectedLocation.price_overrides;

          this.offices = [];

          sizes.forEach((s, i) => {
            const applicable = applicableSizes.find(
                // eslint-disable-next-line radix
                (size) => parseInt(s.size) === size
            );

            if (!applicable) return;

            const priceOverride = priceOverrides.find((po) => parseInt(s.size) === po.size);

            if (priceOverride) {
              sizes[i].price = parseFloat(priceOverride.price);
            }

            this.offices.push(s);
          });

          this.sizeOptions.max = this.offices.length > 0 ? this.offices.length - 1 : 1;

          const defaultIndex = this.offices.length > 0 ? this.offices.length / 2 : 0;

          this.swapSpace(defaultIndex);
        },
        getCsrfAwareParams(additional = {}) {
          const params = new URLSearchParams();

          params.append(window.csrfTokenName, window.csrfTokenValue);

          Object.entries(additional)
              .forEach((p) => {
                const key = p[0];
                const val = p[1];

                if (typeof val === 'object' && val != null) {
                  params.append(key, JSON.stringify(val));
                } else {
                  params.append(key, val || '');
                }
              });

          return params;
        },
        findLocationByName(name) {
          return this.locationList.find((l) => l.Sitename.toLowerCase() == name.toLowerCase());
        },
        findLocation(id) {
          return this.locationList.find((l) => l.id == id);
        },
        formatPrice(price, discount = 0) {
          return PriceFormatter(price, {
            'discount_percentage': discount
          });
        },
        getError(fieldName) {
          if (typeof this.formErrors[fieldName] !== 'undefined') {
            return this.formErrors[fieldName][0];
          }

          return null;
        },
        swapSpace(val) {
          const index = parseInt(val);
          this.form.selectedSpace = this.offices[index];
          this.form.sizeSlider = index;
        },
        updateStepState(steps, state) {
          steps.forEach((s) => this.steps[s].state = state);
        },
        setPrices(unit) {
          this.form.fullPrice = this.formatPrice(unit.weekRate);
          this.form.discountedPrice = this.formatPrice(unit.weekRate, this.discount.percentage);
        },
        trackConversion() {
          gtag('event', 'conversion', { 'send_to': 'AW-860419051/L3fECLashZMBEOvno5oD' });
          fbq('track', 'Lead', { value: 1, currency: '1', });
        },
        getPreviousQuote(urlParams) {
          const fromUrl = urlParams.get('previous_quote');

          if (fromUrl) {
            return fromUrl;
          }
          return Cookies.get('previous_quote');
        },
      },
      watch: {
        'form.selectedLocation': function (site, prev) {
          const params = this.getCsrfAwareParams({ 'id': site.id });
          const sizesPromise = axios.post('/actions/businessfirst/calculator/office-sizes', params);

          this.updateStepState(['2'], 'loading');

          sizesPromise.then((r) => {
            this.handleUnitSizesResponse(r);
            this.updateStepState(['1', '2'], 'ready');
          }).catch((error) => {
            this.$snotify.error(
                'Unable to reach with the server. Please refresh the page to try again. If the problem persists, please contact support',
                'Error'
            );
          });
        },
        'form.sizeSlider': function (value) {
          this.swapSpace(value);
        },
        'moveIn.date': function (val) {
          this.form.moveInDate = DateFormatter(val);
        }
      },
    });

    app.use(Snotify, {
      toast: {
        position: 'centerTop'
      }
    });

    app.mount(el);
  }
};

export default priceCalculator;
